import { useState, useEffect } from 'react';
import { getTewariBox, saveTewariDecisions, getSuppliers, getProducts } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Package, Calendar, Check, Truck, Archive, ShoppingCart, Plus, Trash2, BoxSelect } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

export const TewariBox = () => {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [items, setItems] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [stockOrders, setStockOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [tewariProducts, setTewariProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tewariRes, suppliersRes, productsRes] = await Promise.all([
        getTewariBox(selectedDate),
        getSuppliers(),
        getProducts(),
      ]);
      setItems(tewariRes.data.items || []);
      setSuppliers(suppliersRes.data || []);
      setTewariProducts((productsRes.data || []).filter(p => p.is_tewari));
      const decMap = {};
      (tewariRes.data.decisions || []).forEach(d => {
        decMap[`${d.order_id}-${d.item_index}`] = {
          decision: d.decision || '',
          supplier_id: d.supplier_id || '',
          supplier_name: d.supplier_name || '',
        };
      });
      setDecisions(decMap);
      setStockOrders(tewariRes.data.stock_orders || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const setDecisionFor = (key, decision) => {
    setDecisions(prev => ({ ...prev, [key]: { ...prev[key], decision } }));
  };

  const setSupplierForItem = (key, supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setDecisions(prev => ({ ...prev, [key]: { ...prev[key], supplier_id: supplierId, supplier_name: supplier?.name || '' } }));
  };

  const addStockOrder = (product) => {
    const supplier = suppliers.find(s => s.id === product.supplier_id);
    setStockOrders(prev => [...prev, {
      id: `stock-${Date.now()}`,
      item_id: product.id,
      item_name: product.name,
      origin: product.origin || '',
      spec: product.spec || '',
      quantity: 1,
      supplier_id: product.supplier_id || '',
      supplier_name: supplier?.name || '',
    }]);
  };

  const updateStockOrder = (id, field, value) => {
    setStockOrders(prev => prev.map(o => o.id === id ? { ...o, [field]: value } : o));
  };

  const updateStockSupplier = (id, supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setStockOrders(prev => prev.map(o => o.id === id ? { ...o, supplier_id: supplierId, supplier_name: supplier?.name || '' } : o));
  };

  const removeStockOrder = (id) => {
    setStockOrders(prev => prev.filter(o => o.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const decisionsList = items.map(item => {
        const key = `${item.order_id}-${item.item_index}`;
        const dec = decisions[key] || {};
        return {
          order_id: item.order_id, item_index: item.item_index, item_id: item.item_id,
          item_name: item.item_name, customer_name: item.customer_name, customer_code: item.customer_code,
          quantity: item.quantity, origin: item.origin, spec: item.spec,
          original_supplier_id: item.original_supplier_id, original_supplier_name: item.original_supplier_name,
          decision: dec.decision || '',
          supplier_id: dec.supplier_id || item.original_supplier_id || '',
          supplier_name: dec.supplier_name || item.original_supplier_name || '',
        };
      });
      await saveTewariDecisions({ date: selectedDate, decisions: decisionsList, stock_orders: stockOrders });
      toast.success('一括保存しました');
    } catch (error) {
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 個別保存（1アイテムだけ保存）
  const handleSaveSingle = async (item) => {
    const key = `${item.order_id}-${item.item_index}`;
    const dec = decisions[key] || {};
    if (!dec.decision) {
      toast.error('発注または在庫対応を選択してください');
      return;
    }
    try {
      // 全decisionリストを構築して送信（APIは全件上書き）
      const decisionsList = items.map(i => {
        const k = `${i.order_id}-${i.item_index}`;
        const d = decisions[k] || {};
        return {
          order_id: i.order_id, item_index: i.item_index, item_id: i.item_id,
          item_name: i.item_name, customer_name: i.customer_name, customer_code: i.customer_code,
          quantity: i.quantity, origin: i.origin, spec: i.spec,
          original_supplier_id: i.original_supplier_id, original_supplier_name: i.original_supplier_name,
          decision: d.decision || '',
          supplier_id: d.supplier_id || i.original_supplier_id || '',
          supplier_name: d.supplier_name || i.original_supplier_name || '',
        };
      });
      await saveTewariDecisions({ date: selectedDate, decisions: decisionsList, stock_orders: stockOrders });
      toast.success(`${item.item_name}を保存しました`);
    } catch (error) {
      toast.error('保存に失敗しました');
    }
  };

  const undecidedCount = items.filter(i => !decisions[`${i.order_id}-${i.item_index}`]?.decision).length;
  const orderCount = items.filter(i => decisions[`${i.order_id}-${i.item_index}`]?.decision === '発注').length;
  const stockCount = items.filter(i => decisions[`${i.order_id}-${i.item_index}`]?.decision === '在庫対応').length;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="tewari-box-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">手割りボックス</h1>
          <p className="text-slate-500 mt-1">手割り商品の発注・在庫対応を判定</p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="flex items-center gap-2"><Calendar className="h-4 w-4" />配送日:</Label>
          <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-40" />
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            <Check className="h-4 w-4 mr-2" />{saving ? '保存中...' : '一括保存'}
          </Button>
        </div>
      </div>

      {undecidedCount > 0 && (
        <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4 flex items-center justify-center gap-3">
          <span className="text-red-600 font-black text-xl">未登録あり（{undecidedCount}件）</span>
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        {orderCount > 0 && <Badge className="bg-blue-100 text-blue-800 text-sm px-3 py-1">発注 {orderCount}</Badge>}
        {stockCount > 0 && <Badge className="bg-amber-100 text-amber-800 text-sm px-3 py-1">在庫対応 {stockCount}</Badge>}
        {stockOrders.length > 0 && <Badge className="bg-purple-100 text-purple-800 text-sm px-3 py-1">在庫用発注 {stockOrders.length}</Badge>}
        {items.length === 0 && stockOrders.length === 0 && <Badge variant="outline" className="text-slate-400 text-sm px-3 py-1">手割り対象なし</Badge>}
      </div>

      {/* 注文商品の判定 */}
      {items.length > 0 && (
        <div className="space-y-3">
          {items.map((item) => {
            const key = `${item.order_id}-${item.item_index}`;
            const dec = decisions[key] || {};
            const isOrder = dec.decision === '発注';
            const isStock = dec.decision === '在庫対応';
            const currentSupplierId = dec.supplier_id || item.original_supplier_id || '';
            return (
              <Card key={key} className={`border-l-4 ${isOrder ? 'border-l-blue-500' : isStock ? 'border-l-amber-500' : 'border-l-orange-300'}`}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-base">{item.item_name}</span>
                        <span className="text-sm text-slate-500">{[item.origin, item.spec].filter(Boolean).join('/') || ''}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><ShoppingCart className="h-3.5 w-3.5" />{item.customer_name}{item.customer_code ? ` (${item.customer_code})` : ''}</span>
                        <span className="font-mono font-bold text-slate-800">x{item.quantity}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setDecisionFor(key, '発注')} className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center gap-1.5 ${isOrder ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                        <Truck className="h-4 w-4" />発注
                      </button>
                      <button onClick={() => setDecisionFor(key, '在庫対応')} className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center gap-1.5 ${isStock ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                        <Archive className="h-4 w-4" />在庫対応
                      </button>
                    </div>
                    {isOrder && (
                      <div className="min-w-[180px]">
                        <Select value={currentSupplierId} onValueChange={(v) => setSupplierForItem(key, v)}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="仕入れ先" /></SelectTrigger>
                          <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                    {isStock && <Badge className="bg-amber-100 text-amber-700 text-xs">在庫から出荷</Badge>}
                    {!isOrder && !isStock && <Badge variant="outline" className="border-red-400 text-red-600 text-xs font-bold">未登録</Badge>}
                    {/* 個別保存 */}
                    {(isOrder || isStock) && (
                      <Button variant="outline" size="sm" onClick={() => handleSaveSingle(item)} className="text-xs">
                        <Check className="h-3.5 w-3.5 mr-1" />保存
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* 在庫用発注セクション */}
      <Card className="border-purple-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base text-purple-700">
              <BoxSelect className="h-5 w-5" />
              在庫用発注
            </CardTitle>
            {tewariProducts.length > 0 && (
              <Select onValueChange={(pid) => { const p = tewariProducts.find(x => x.id === pid); if (p) addStockOrder(p); }}>
                <SelectTrigger className="w-48 h-9 text-sm"><SelectValue placeholder="商品を追加..." /></SelectTrigger>
                <SelectContent>{tewariProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {stockOrders.length === 0 ? (
            <p className="text-sm text-slate-400 py-3 text-center">在庫用発注はありません</p>
          ) : (
            <div className="space-y-2">
              {stockOrders.map(order => (
                <div key={order.id} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                  <div className="flex-1 min-w-[150px]">
                    <span className="font-medium text-sm">{order.item_name}</span>
                    {(order.origin || order.spec) && <span className="text-xs text-slate-500 ml-2">{[order.origin, order.spec].filter(Boolean).join('/')}</span>}
                  </div>
                  <Input
                    type="number"
                    value={order.quantity}
                    onChange={(e) => updateStockOrder(order.id, 'quantity', parseFloat(e.target.value) || 0)}
                    className="w-20 h-8 text-right text-sm"
                  />
                  <Select value={order.supplier_id || ''} onValueChange={(v) => updateStockSupplier(order.id, v)}>
                    <SelectTrigger className="w-36 h-8 text-sm"><SelectValue placeholder="仕入れ先" /></SelectTrigger>
                    <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={() => removeStockOrder(order.id)} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  <Button variant="outline" size="sm" onClick={handleSave} className="text-xs"><Check className="h-3.5 w-3.5 mr-1" />保存</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
